import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getProfilePicture } from '../utils/profilePicture';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface TeamJoinRequest {
  id: string;
  playerId: string;
  teamId: string;
  leagueId?: string;
  status: 'pending' | 'approved' | 'rejected';
  message?: string;
  requestedAt: string;
  player?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    profilePicture?: string;
    soccerExperience?: string;
  };
  team?: {
    id: string;
    name: string;
    teamNumber: string;
  };
  league?: {
    id: string;
    name: string;
    season: string;
  };
}

const TeamJoinApprovals: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<TeamJoinRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && (user.profileType === 'manager' || isAdminOrSuperAdmin(user.profileType))) {
      fetchRequests();
    }
  }, [user]);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/team-join-requests/pending');
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching join requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId: string, status: 'approved' | 'rejected') => {
    try {
      await api.patch(`/team-join-requests/${requestId}/status`, { status });
      alert(`Request ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchRequests();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update request status');
    }
  };

  if (!user || (user.profileType !== 'manager' && !isAdminOrSuperAdmin(user.profileType))) {
    return <div className="container">Access denied. Managers and admins only.</div>;
  }

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <h1>Team Join Request Approvals</h1>
      <p style={{ color: '#757575', marginBottom: '20px' }}>
        Review and approve or reject player requests to join teams
      </p>

      {requests.length === 0 ? (
        <div className="card">
          <p>No pending team join requests.</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Team</th>
                <th>League</th>
                <th>Experience</th>
                <th>Message</th>
                <th>Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td>
                    {request.player ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img 
                          src={getProfilePicture(request.player)} 
                          alt={`${request.player.firstName} ${request.player.lastName}`}
                          style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {request.player.firstName} {request.player.lastName}
                          </div>
                          <div style={{ fontSize: '12px', color: '#757575' }}>
                            {request.player.email}
                          </div>
                        </div>
                      </div>
                    ) : (
                      'Unknown Player'
                    )}
                  </td>
                  <td>
                    {request.team ? (
                      <a href={`/teams/${request.teamId}`} style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}>
                        {request.team.name} ({request.team.teamNumber})
                      </a>
                    ) : (
                      'Unknown Team'
                    )}
                  </td>
                  <td>
                    {request.league ? request.league.name : '-'}
                  </td>
                  <td>{request.player?.soccerExperience || 'N/A'}</td>
                  <td>{request.message || '-'}</td>
                  <td>{new Date(request.requestedAt).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleStatusChange(request.id, 'approved')}
                        style={{ fontSize: '12px', padding: '5px 10px' }}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleStatusChange(request.id, 'rejected')}
                        style={{ fontSize: '12px', padding: '5px 10px' }}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamJoinApprovals;

